import { useContext, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { AuthContext } from "../../../context/AuthContext";
import { ErrorContext } from "../../../context/ErrorContext";
import { BulkCoupon, LicenseType } from "../../../collector";
import Switch from "../../../components/Switch/Switch";
import EnabledLabel from "../../../components/EnabledLabel";

interface EditBulkCouponProps {
    environment: string;
    editedBulkCoupon: () => void;
    bulkCouponId: number | null;
    close: () => void;
}

export default function EditBulkCouponModal(props: EditBulkCouponProps) {
    const [bulkCoupon, setBulkCoupon] = useState<BulkCoupon | null>(null);
    const [show, setShow] = useState<boolean>(false);
    const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(false);
    const [changeCode, setChangeCode] = useState<boolean>(false);

    const { showErrorModal } = useContext(ErrorContext);
    const { token, invalidWSToken } = useContext(AuthContext);

    const handleClose = () => {
        setAutoGenerateCode(false);
        setChangeCode(false);
        props.close();
        setShow(false);
    }
    const handleShow = () => setShow(true);

    useEffect(() => {
        if (props.bulkCouponId) {
            fetchBulkCoupon();
            handleShow();
        }
    }, [props.bulkCouponId])

    const editBulkCoupon = async () => {
        try {
            if (!bulkCoupon) throw Error("Bulk coupon was null...");
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token(),
                },
                body: JSON.stringify({
                    'id': bulkCoupon.id,
                    'delimiter': bulkCoupon.delimiter,
                    'value': bulkCoupon.discount,
                    'product_id': bulkCoupon.productId,
                    'enabled': bulkCoupon.enabled,
                    'max_uses': bulkCoupon.maxUses,
                    'autoGenerateCode': autoGenerateCode,
                    'code': bulkCoupon.code,
                    'change_code': changeCode
                })
            }
            await fetch(props.environment + 'editBulkCoupon', requestOptions)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error("Failed to edit bulk coupon");
                    }
                    return res.json();
                })
                .then(() => {
                    props.editedBulkCoupon();
                    handleClose();
                });
        } catch (error: any) {
            showErrorModal(error ?? "Failed to edit bulk coupon - Server is unavailable at the moment");
        }
    }

    const fetchBulkCoupon = async () => {
        try {
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token(),
                },
                body: JSON.stringify({
                    'id': props.bulkCouponId
                })
            }
            await fetch(props.environment + 'getBulkCoupon', requestOptions)
                .then((res) => {
                    if (!res.ok) {
                        throw new Error("Failed to fetch bulk coupon");
                    }
                    return res.json();
                })
                .then(({ bulkCoupon }) => {
                    setBulkCoupon(bulkCoupon);
                });
        } catch (error: any) {
            showErrorModal(error ?? "Failed to fetch bulk coupon - Server is unavailable at the moment");
        }
    }

    return (
        <>
            {show && bulkCoupon && !invalidWSToken(props.environment) &&
                <Modal
                    show={show}
                    backdrop="static"
                    onHide={() => handleClose()}
                    keyboard={false}
                >
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        editBulkCoupon();
                    }}>
                        <Modal.Header closeButton>
                            <Modal.Title>Edit Bulk Coupon</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="dialog-data-field">
                                <span>Product:</span>
                                <select value={bulkCoupon.productId} required
                                    onChange={(item) => setBulkCoupon({ ...bulkCoupon, productId: Number(item.target.value) })}
                                >
                                    <option value={LicenseType.Pro}>DOTE Pro</option>
                                    <option value={LicenseType.ProCommunity}>DOTE Pro Community</option>
                                    <option value={LicenseType.SiteLicense}>Site-License</option>
                                </select>
                            </div>
                            <div className="dialog-data-field">
                                <span>Delimiter:</span>
                                <input type="number" min={2} value={bulkCoupon.delimiter} required
                                    onChange={(item) => setBulkCoupon({ ...bulkCoupon, delimiter: Number(item.target.value) })} />
                            </div>
                            <div className="dialog-data-field">
                                <span>Discount Value (%):</span>
                                <input min={0} max={99} type='number' value={bulkCoupon.discount} required
                                    onChange={(item) => setBulkCoupon({ ...bulkCoupon, discount: Number(item.target.value) })}
                                />
                            </div>
                            <div className="dialog-data-field">
                                <span>Max Uses (0 = infinite):</span>
                                <input min={0} type='number' value={bulkCoupon.maxUses} required
                                    onChange={(item) => setBulkCoupon({ ...bulkCoupon, maxUses: Number(item.target.value) })}
                                />
                            </div>
                            <div className="dialog-data-field">
                                <span>Change Code: (This will enable more fields)</span>
                                <Switch checked={changeCode} onChange={(item) => setChangeCode(item.target.checked)} />
                            </div>
                            {changeCode &&
                                <div className="dialog-data-field" style={{ alignItems: 'center' }}>
                                    <span style={{ alignSelf: 'flex-start' }}>Code:</span>
                                    <div style={{ display: 'flex', flexDirection: 'column', rowGap: '5px' }}>
                                        <div style={{ display: 'flex', columnGap: '3px', justifyContent: 'flex-end' }}>
                                            <Button style={{ opacity: (autoGenerateCode ? '1' : '0.5') }} onClick={() => setAutoGenerateCode(!autoGenerateCode)} >
                                                Auto generate code
                                            </Button>
                                        </div>
                                        {!autoGenerateCode &&
                                            <input placeholder="Write code here..." value={bulkCoupon.code} required
                                                onChange={(item) => setBulkCoupon({ ...bulkCoupon, code: item.target.value.toLowerCase() })}
                                            />
                                        }
                                    </div>
                                </div>
                            }
                            <div className="dialog-data-field">
                                <span>Enabled:</span>
                                <Switch checked={bulkCoupon.enabled} onChange={(item) => setBulkCoupon({ ...bulkCoupon, enabled: item.target.checked })} />
                            </div>
                            <div className="text-end-container">
                                <EnabledLabel enabled={bulkCoupon.enabled} />
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => handleClose()}>
                                Close
                            </Button>
                            <Button variant="primary" type='submit'>Save</Button>
                        </Modal.Footer>
                    </form>
                </Modal>}
        </>
    );
}
